import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Settings, Plus, Trash2, Loader2, UserCog, XCircle, Mail, Edit2, AlertTriangle, Lock, Shield, RotateCcw, Users, Unlock, User, Save, Eye, EyeOff, Key, Check } from 'lucide-react';
import { toast } from 'sonner';

// Permission labels in Italian
const PERMISSION_LABELS = {
  patients_view: 'Visualizza Pazienti',
  patients_edit: 'Modifica Pazienti',
  patients_discharge: 'Libera Camera',
  archive_view: 'Visualizza Archivio',
  menu_view: 'Visualizza Menu',
  menu_edit: 'Modifica Menu',
  orders_view: 'Visualizza Comande',
  orders_edit: 'Modifica Comande',
  kitchen_view: 'Visualizza Riepilogo',
  patient_sheets_view: 'Visualizza Cartellini',
  patient_sheets_print: 'Stampa Cartellini',
  patient_sheets_edit: 'Modifica Cartellini',
  history_view: 'Visualizza Storico',
  users_edit: 'Modifica Sottoposti',
  // Settings permissions
  settings_dishes_view: 'Visualizza Piatti',
  settings_dishes_edit: 'Modifica Piatti',
  settings_categories_view: 'Visualizza Categorie',
  settings_categories_edit: 'Modifica Categorie',
  settings_diets_view: 'Visualizza Diete',
  settings_diets_edit: 'Modifica Diete',
  settings_allergies_view: 'Visualizza Allergeni',
  settings_allergies_edit: 'Modifica Allergeni'
};

// Permission categories for grouping
const PERMISSION_CATEGORIES = {
  'Pazienti': ['patients_view', 'patients_edit', 'patients_discharge'],
  'Archivio': ['archive_view'],
  'Menu': ['menu_view', 'menu_edit'],
  'Comande': ['orders_view', 'orders_edit'],
  'Riepilogo': ['kitchen_view'],
  'Cartellini': ['patient_sheets_view', 'patient_sheets_print', 'patient_sheets_edit'],
  'Storico': ['history_view'],
  'Utenti': ['users_edit'],
  'Impostazioni - Piatti': ['settings_dishes_view', 'settings_dishes_edit', 'settings_categories_view', 'settings_categories_edit'],
  'Impostazioni - Diete': ['settings_diets_view', 'settings_diets_edit'],
  'Impostazioni - Allergeni': ['settings_allergies_view', 'settings_allergies_edit']
};

export const AdminPage = () => {
  const { 
    api, 
    user,
    isSuperAdmin, 
    isAdmin,
    isAdminStruttura,
    structureName,
    canManageUsers,
    canViewSettingsDiets,
    canEditSettingsDiets,
    canViewSettingsAllergies,
    canEditSettingsAllergies
  } = useAuth();
  const [users, setUsers] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [diets, setDiets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  
  // Profile form states (for personal settings)
  const [profileEmail, setProfileEmail] = useState('');
  const [profileEmailLoading, setProfileEmailLoading] = useState(false);
  const [profileEmailSaved, setProfileEmailSaved] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form states
  const [userForm, setUserForm] = useState({ username: '', password: '', role: 'operatore', email: '' });
  const [allergyForm, setAllergyForm] = useState({ name: '' });
  const [dietForm, setDietForm] = useState({ name: '' });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [allergyDialogOpen, setAllergyDialogOpen] = useState(false);
  const [dietDialogOpen, setDietDialogOpen] = useState(false);
  
  // Edit user email state
  const [editingUserId, setEditingUserId] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  
  // Permissions dialog state
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Build requests based on permissions
      const requests = [];
      const requestMap = {};
      
      // Users: only if can manage users (admin/superadmin)
      if (canManageUsers) {
        requestMap.users = requests.length;
        requests.push(api.get('/users'));
      }
      
      // Allergies: only if can view
      if (canViewSettingsAllergies) {
        requestMap.allergies = requests.length;
        requests.push(api.get('/allergies'));
      }
      
      // Diets: only if can view
      if (canViewSettingsDiets) {
        requestMap.diets = requests.length;
        requests.push(api.get('/diets'));
      }
      
      const results = await Promise.all(requests);
      
      // Map results back
      if (requestMap.users !== undefined) setUsers(results[requestMap.users].data);
      if (requestMap.allergies !== undefined) setAllergies(results[requestMap.allergies].data.sort((a, b) => a.number - b.number));
      if (requestMap.diets !== undefined) setDiets(results[requestMap.diets].data.sort((a, b) => a.number - b.number));
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [api, canManageUsers, canViewSettingsAllergies, canViewSettingsDiets]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load profile email
  useEffect(() => {
    if (user?.email) {
      setProfileEmail(user.email);
    }
  }, [user]);

  // Profile handlers (personal settings)
  const handleSaveProfileEmail = async () => {
    if (profileEmail && !/^[\w.-]+@[\w.-]+\.\w+$/.test(profileEmail)) {
      toast.error('Formato email non valido');
      return;
    }
    setProfileEmailLoading(true);
    try {
      await api.put('/auth/profile', { email: profileEmail || null });
      toast.success('Email aggiornata');
      setProfileEmailSaved(true);
      setTimeout(() => setProfileEmailSaved(false), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setProfileEmailLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!passwordForm.currentPassword) errors.currentPassword = 'Obbligatorio';
    if (!passwordForm.newPassword) errors.newPassword = 'Obbligatorio';
    else if (passwordForm.newPassword.length < 4) errors.newPassword = 'Minimo 4 caratteri';
    if (passwordForm.newPassword !== passwordForm.confirmPassword) errors.confirmPassword = 'Le password non coincidono';
    
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password aggiornata');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore';
      if (msg.includes('attuale')) setPasswordErrors({ currentPassword: msg });
      else toast.error(msg);
    } finally {
      setPasswordLoading(false);
    }
  };

  // User handlers
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!userForm.username || !userForm.password) {
      toast.error('Username e password obbligatori');
      return;
    }
    try {
      await api.post('/auth/register', userForm);
      toast.success('Utente creato');
      setUserDialogOpen(false);
      setUserForm({ username: '', password: '', role: 'operatore', email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Eliminare ${user.username}?`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      toast.success('Utente eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleToggleUserActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'Utente disattivato' : 'Utente attivato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleSaveEmail = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { email: editEmail || null });
      toast.success('Email aggiornata');
      setEditingUserId(null);
      setEditEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const startEditEmail = (user) => {
    setEditingUserId(user.id);
    setEditEmail(user.email || '');
  };

  // Permissions handlers
  const openPermissionsDialog = async (user) => {
    setSelectedUser(user);
    try {
      const response = await api.get(`/users/${user.id}/permissions`);
      setUserPermissions(response.data.permissions);
      setPermissionsDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento permessi');
    }
  };

  const handlePermissionChange = (key, value) => {
    setUserPermissions(prev => ({ ...prev, [key]: value }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    setSavingPermissions(true);
    try {
      await api.put(`/users/${selectedUser.id}/permissions`, { permissions: userPermissions });
      toast.success('Permessi salvati');
      setPermissionsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleResetPermissions = async () => {
    if (!selectedUser) return;
    if (!window.confirm('Ripristinare i permessi di default per questo ruolo?')) return;
    setSavingPermissions(true);
    try {
      const response = await api.post(`/users/${selectedUser.id}/permissions/reset`);
      setUserPermissions(response.data.permissions);
      toast.success('Permessi ripristinati ai valori di default');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel ripristino');
    } finally {
      setSavingPermissions(false);
    }
  };

  // Allergy handlers
  const handleCreateAllergy = async (e) => {
    e.preventDefault();
    if (!allergyForm.name) {
      toast.error('Nome obbligatorio');
      return;
    }
    try {
      const code = allergyForm.name.toLowerCase().replace(/\s+/g, '_');
      await api.post('/allergies', { 
        name: allergyForm.name.trim(), 
        code,
        is_trace: false 
      });
      toast.success('Allergene aggiunto');
      setAllergyDialogOpen(false);
      setAllergyForm({ name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleDeleteAllergy = async (allergy) => {
    if (allergy.is_default) {
      toast.error('Gli allergeni EU di default non possono essere eliminati');
      return;
    }
    if (!window.confirm(`Eliminare ${allergy.name}?`)) return;
    try {
      await api.delete(`/allergies/${allergy.id}`);
      toast.success('Allergene eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  // Diet handlers
  const handleCreateDiet = async (e) => {
    e.preventDefault();
    if (!dietForm.name) {
      toast.error('Nome obbligatorio');
      return;
    }
    try {
      const code = dietForm.name.toLowerCase().replace(/\s+/g, '_');
      await api.post('/diets', { 
        name: dietForm.name.trim(), 
        code
      });
      toast.success('Dieta aggiunta');
      setDietDialogOpen(false);
      setDietForm({ name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleDeleteDiet = async (diet) => {
    if (diet.is_default) {
      toast.error('Le diete di default non possono essere eliminate');
      return;
    }
    if (!window.confirm(`Eliminare ${diet.name}?`)) return;
    try {
      await api.delete(`/diets/${diet.id}`);
      toast.success('Dieta eliminata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  // Toggle lock handlers (Super Admin only)
  const handleToggleAllergyLock = async (allergy) => {
    try {
      const response = await api.put(`/allergies/${allergy.id}/toggle-lock`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio stato');
    }
  };

  const handleToggleDietLock = async (diet) => {
    try {
      const response = await api.put(`/diets/${diet.id}/toggle-lock`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio stato');
    }
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    admin_struttura: 'Admin Struttura',
    operatore: 'Operatore',
    cucina: 'Cucina'
  };

  const roleColors = {
    super_admin: 'bg-purple-100 text-purple-700',
    admin_struttura: 'bg-blue-100 text-blue-700',
    operatore: 'bg-green-100 text-green-700',
    cucina: 'bg-orange-100 text-orange-700'
  };

  // Available roles for admin struttura to create
  const availableRoles = isSuperAdmin 
    ? ['admin_struttura', 'operatore', 'cucina']
    : ['operatore', 'cucina'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  // Calculate visible tabs
  // Tab "Utenti" shows as "Privacy e sicurezza" for operatore/cucina (they only see their profile)
  const usersTabLabel = canManageUsers ? 'Utenti' : 'Privacy e sicurezza';
  
  const visibleTabs = [
    { id: 'users', label: usersTabLabel, icon: UserCog, visible: true }, // Always visible for personal settings
    { id: 'diets', label: 'Diete', icon: Users, visible: canViewSettingsDiets },
    { id: 'allergies', label: 'Allergeni', icon: AlertTriangle, visible: canViewSettingsAllergies }
  ].filter(tab => tab.visible);

  const gridCols = visibleTabs.length === 4 ? 'grid-cols-4' : 
                   visibleTabs.length === 3 ? 'grid-cols-3' : 
                   visibleTabs.length === 2 ? 'grid-cols-2' : 'grid-cols-1';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="w-7 h-7 text-blue-700" />
          Impostazioni
        </h1>
        <p className="text-slate-500 mt-1">
          {structureName ? `Gestisci impostazioni per ${structureName}` : 'Gestisci le tue impostazioni'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${gridCols} h-auto p-1 bg-slate-100 rounded-lg`}>
          {visibleTabs.map(tab => (
            <TabsTrigger 
              key={tab.id}
              value={tab.id} 
              className="data-[state=active]:bg-white py-2"
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <div className="space-y-6">
            {/* Personal Profile Section - visible to everyone */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-sky-600 rounded-xl flex items-center justify-center text-white">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle>Il mio Profilo</CardTitle>
                    <CardDescription>Gestisci le tue informazioni personali</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Email Section */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-emerald-600" />
                    Email di Recupero
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={profileEmail}
                      onChange={(e) => {
                        setProfileEmail(e.target.value);
                        setProfileEmailSaved(false);
                      }}
                      placeholder="nome@esempio.com"
                      className="flex-1 max-w-sm"
                      data-testid="profile-email-input"
                    />
                    <Button
                      onClick={handleSaveProfileEmail}
                      disabled={profileEmailLoading}
                      className={`${profileEmailSaved ? 'bg-emerald-500' : 'bg-emerald-600 hover:bg-emerald-700'} min-w-[44px]`}
                      data-testid="save-profile-email-btn"
                    >
                      {profileEmailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                       profileEmailSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400">Usata per recuperare la password in caso di smarrimento</p>
                </div>

                {/* Password Section */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-blue-600" />
                    Cambia Password
                  </Label>
                  <form onSubmit={handleChangePassword} className="space-y-3 max-w-sm">
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => {
                          setPasswordForm({...passwordForm, currentPassword: e.target.value});
                          setPasswordErrors({...passwordErrors, currentPassword: null});
                        }}
                        placeholder="Password attuale"
                        className={`pr-10 ${passwordErrors.currentPassword ? 'border-red-500' : ''}`}
                        data-testid="profile-current-password"
                      />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.currentPassword && <p className="text-xs text-red-500">{passwordErrors.currentPassword}</p>}
                    
                    <div className="relative">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => {
                          setPasswordForm({...passwordForm, newPassword: e.target.value});
                          setPasswordErrors({...passwordErrors, newPassword: null});
                        }}
                        placeholder="Nuova password"
                        className={`pr-10 ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
                        data-testid="profile-new-password"
                      />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowNewPassword(!showNewPassword)}>
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.newPassword && <p className="text-xs text-red-500">{passwordErrors.newPassword}</p>}
                    
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => {
                          setPasswordForm({...passwordForm, confirmPassword: e.target.value});
                          setPasswordErrors({...passwordErrors, confirmPassword: null});
                        }}
                        placeholder="Conferma nuova password"
                        className={`pr-10 ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
                        data-testid="profile-confirm-password"
                      />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && <p className="text-xs text-red-500">{passwordErrors.confirmPassword}</p>}
                    
                    <Button type="submit" disabled={passwordLoading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="profile-change-password-btn">
                      {passwordLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                      Aggiorna Password
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>

            {/* Users List - only visible to Admin/SuperAdmin */}
            {canManageUsers && (
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Utenti della Struttura</CardTitle>
                    <CardDescription>Gestisci gli accessi degli operatori</CardDescription>
                  </div>
                  <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-700 hover:bg-blue-800" data-testid="add-user-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nuovo Utente
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Nuovo Utente</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateUser} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Username</Label>
                          <Input
                            value={userForm.username}
                            onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                            placeholder="nome.utente"
                            data-testid="new-user-username"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Password</Label>
                          <Input
                            type="password"
                            value={userForm.password}
                            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                            placeholder="Password sicura"
                            data-testid="new-user-password"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Ruolo</Label>
                          <Select
                            value={userForm.role}
                            onValueChange={(v) => setUserForm({ ...userForm, role: v })}
                          >
                            <SelectTrigger data-testid="new-user-role">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableRoles.map(role => (
                                <SelectItem key={role} value={role}>
                                  {roleLabels[role]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Email (per recupero password)</Label>
                          <Input
                            type="email"
                            value={userForm.email}
                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            placeholder="email@esempio.com (opzionale)"
                            data-testid="new-user-email"
                          />
                        </div>
                        <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800" data-testid="save-user-btn">
                          Crea Utente
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {users.map(u => (
                      <div 
                        key={u.id}
                        className={`p-4 rounded-lg border ${u.is_active !== false ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}
                        data-testid={`user-item-${u.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${u.is_active !== false ? 'bg-blue-100' : 'bg-slate-200'}`}>
                              <Users className={`w-5 h-5 ${u.is_active !== false ? 'text-blue-700' : 'text-slate-400'}`} />
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900 flex items-center gap-2">
                                {u.username}
                                {u.is_active === false && <XCircle className="w-4 h-4 text-slate-400" />}
                              </div>
                              <Badge className={`mt-1 ${roleColors[u.role] || 'bg-slate-100'}`}>
                                {roleLabels[u.role] || u.role}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Email section */}
                          <div className="flex-1 min-w-0 px-2">
                            {editingUserId === u.id ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="email"
                                  value={editEmail}
                                  onChange={(e) => setEditEmail(e.target.value)}
                                  placeholder="email@esempio.com"
                                  className="h-8 text-sm flex-1 min-w-[180px]"
                                  data-testid={`edit-email-input-${u.id}`}
                                />
                                <Button size="sm" onClick={() => handleSaveEmail(u)} className="h-8 bg-blue-700 hover:bg-blue-800">
                                  Salva
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingUserId(null)} className="h-8">
                                  Annulla
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Mail className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{u.email || 'Nessuna email'}</span>
                                {/* Super Admin can edit all emails, Admin Struttura can edit operatore/cucina emails */}
                                {(isSuperAdmin || 
                                  (!isSuperAdmin && u.role !== 'super_admin' && u.role !== 'admin_struttura')) && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0"
                                    onClick={() => startEditEmail(u)}
                                    data-testid={`edit-email-btn-${u.id}`}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Permissions button for operatore and cucina */}
                            {(u.role === 'operatore' || u.role === 'cucina') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openPermissionsDialog(u)}
                                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                data-testid={`permissions-user-${u.id}`}
                              >
                                <Shield className="w-4 h-4 mr-1" />
                                Permessi
                              </Button>
                            )}
                            
                            {/* Super Admin can manage admin_struttura, admin_struttura can manage operatore/cucina */}
                            {(isSuperAdmin && u.role !== 'super_admin') || 
                             (!isSuperAdmin && u.role !== 'super_admin' && u.role !== 'admin_struttura') ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleUserActive(u)}
                                  data-testid={`toggle-user-${u.id}`}
                                >
                                  {u.is_active !== false ? 'Disattiva' : 'Attiva'}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteUser(u)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Diets Tab */}
        <TabsContent value="diets" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Profilo Nutrizionale (Diete)</CardTitle>
                <CardDescription>
                  {canEditSettingsDiets 
                    ? 'Le 10 diete base sono predefinite. Puoi aggiungerne altre personalizzate.'
                    : 'Visualizza le diete disponibili per i pazienti.'}
                </CardDescription>
              </div>
              {canEditSettingsDiets && (
                <Dialog open={dietDialogOpen} onOpenChange={setDietDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-diet-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuova Dieta
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Nuova Dieta</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateDiet} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nome Dieta</Label>
                        <Input
                          value={dietForm.name}
                          onChange={(e) => setDietForm({ ...dietForm, name: e.target.value })}
                          placeholder="Es: Dieta Vegetariana, Dieta Senza Glutine..."
                          data-testid="new-diet-name"
                        />
                      </div>
                      <p className="text-xs text-slate-500">
                        La nuova dieta sarà disponibile nel profilo nutrizionale dei pazienti.
                      </p>
                      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-diet-btn">
                        Aggiungi Dieta
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {diets.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessuna dieta configurata</p>
              ) : (
                <div className="space-y-2">
                  {diets.map(diet => (
                    <div 
                      key={diet.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${diet.is_default ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
                      data-testid={`diet-item-${diet.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${diet.is_default ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                          {diet.number}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 flex items-center gap-2">
                            {diet.name}
                            {diet.is_default && (
                              <Badge variant="outline" className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300">
                                <Lock className="w-3 h-3 mr-1" />
                                Base
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 font-mono">
                            Codice: {diet.code}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Lock/Unlock button - Super Admin only */}
                        {isSuperAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleDietLock(diet)}
                            className={diet.is_default ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"}
                            title={diet.is_default ? "Sblocca dieta" : "Blocca dieta"}
                            data-testid={`toggle-lock-diet-${diet.id}`}
                          >
                            {diet.is_default ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                          </Button>
                        )}
                        {/* Delete button - only for non-default and if can edit */}
                        {!diet.is_default && canEditSettingsDiets && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteDiet(diet)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-diet-${diet.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Allergies Tab */}
        <TabsContent value="allergies" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Allergeni</CardTitle>
                <CardDescription>
                  {canEditSettingsAllergies 
                    ? 'I 14 allergeni EU sono predefiniti. Puoi aggiungerne altri personalizzati.'
                    : 'Visualizza gli allergeni configurati.'}
                </CardDescription>
              </div>
              {canEditSettingsAllergies && (
                <Dialog open={allergyDialogOpen} onOpenChange={setAllergyDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-600 hover:bg-amber-700" data-testid="add-allergy-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuovo Allergene
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Nuovo Allergene</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateAllergy} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nome Allergene</Label>
                        <Input
                          value={allergyForm.name}
                          onChange={(e) => setAllergyForm({ ...allergyForm, name: e.target.value })}
                        placeholder="Es: Nichel, Kiwi, Fragole..."
                        data-testid="new-allergy-name"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      Il nuovo allergene sarà numerato automaticamente e disponibile sia per i pazienti che per il menu.
                    </p>
                    <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" data-testid="save-allergy-btn">
                      Aggiungi Allergene
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {allergies.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessun allergene configurato</p>
              ) : (
                <div className="space-y-2">
                  {allergies.map(allergy => (
                    <div 
                      key={allergy.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${allergy.is_default ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                      data-testid={`allergy-item-${allergy.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${allergy.is_default ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-700'}`}>
                          {allergy.number}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 flex items-center gap-2">
                            {allergy.name}
                            {allergy.is_default && (
                              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                                <Lock className="w-3 h-3 mr-1" />
                                EU
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 font-mono">
                            Codice: {allergy.number}a / {allergy.number}b (tracce)
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Lock/Unlock button - Super Admin only */}
                        {isSuperAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleAllergyLock(allergy)}
                            className={allergy.is_default ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"}
                            title={allergy.is_default ? "Sblocca allergene" : "Blocca allergene"}
                            data-testid={`toggle-lock-allergy-${allergy.id}`}
                          >
                            {allergy.is_default ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                          </Button>
                        )}
                        {/* Delete button - only for non-default and if can edit */}
                        {!allergy.is_default && canEditSettingsAllergies && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteAllergy(allergy)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-allergy-${allergy.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Permessi: {selectedUser?.username}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Configura cosa può vedere e modificare questo utente ({selectedUser?.role === 'operatore' ? 'Operatore' : 'Cucina'}).
            </p>
            
            {Object.entries(PERMISSION_CATEGORIES).map(([category, keys]) => (
              <div key={category} className="border rounded-lg p-3 bg-slate-50">
                <h4 className="font-medium text-slate-700 mb-2">{category}</h4>
                <div className="space-y-2">
                  {keys.map(key => (
                    <div key={key} className="flex items-center justify-between">
                      <Label htmlFor={key} className="text-sm cursor-pointer">
                        {PERMISSION_LABELS[key]}
                      </Label>
                      <Switch
                        id={key}
                        checked={userPermissions[key] ?? false}
                        onCheckedChange={(checked) => handlePermissionChange(key, checked)}
                        data-testid={`permission-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            <div className="flex gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={handleResetPermissions}
                disabled={savingPermissions}
                className="flex-1"
                data-testid="reset-permissions-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset Default
              </Button>
              <Button 
                onClick={handleSavePermissions}
                disabled={savingPermissions}
                className="flex-1 bg-blue-700 hover:bg-blue-800"
                data-testid="save-permissions-btn"
              >
                {savingPermissions ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Salva Permessi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
